import type { EntityId, ReadonlyVec2, Vec2 } from "@core/types";
import { asEntityId } from "@core/types";
import {
  FLOWER_ENERGY_BONUS as _FLOWER_ENERGY_BONUS,
  FLOWER_USE_RADIUS as _FLOWER_USE_RADIUS,
  GLOWFLY_COLLECT_RADIUS as _GLOWFLY_COLLECT_RADIUS,
  GLOWFLY_ENERGY_BONUS as _GLOWFLY_ENERGY_BONUS,
  GLOWFLY_ORBIT_RADIUS as _GLOWFLY_ORBIT_RADIUS,
  GLOWFLY_ORBIT_SPEED as _GLOWFLY_ORBIT_SPEED,
  SAND_COLLECT_RADIUS as _SAND_COLLECT_RADIUS,
  SAND_ENERGY_BONUS as _SAND_ENERGY_BONUS,
  SEED_COLLECT_RADIUS as _SEED_COLLECT_RADIUS,
  SEED_REVEAL_RADIUS as _SEED_REVEAL_RADIUS,
} from "@data/balance";

/** Балансовые константы — реэкспорт из @data/balance. */
export const SEED_REVEAL_RADIUS: number = _SEED_REVEAL_RADIUS;
export const SEED_COLLECT_RADIUS: number = _SEED_COLLECT_RADIUS;
export const SAND_COLLECT_RADIUS: number = _SAND_COLLECT_RADIUS;
export const FLOWER_USE_RADIUS: number = _FLOWER_USE_RADIUS;
export const SAND_ENERGY_BONUS: number = _SAND_ENERGY_BONUS;
export const FLOWER_ENERGY_BONUS: number = _FLOWER_ENERGY_BONUS;
export const GLOWFLY_COLLECT_RADIUS: number = _GLOWFLY_COLLECT_RADIUS;
export const GLOWFLY_ENERGY_BONUS: number = _GLOWFLY_ENERGY_BONUS;
export const GLOWFLY_ORBIT_RADIUS: number = _GLOWFLY_ORBIT_RADIUS;
export const GLOWFLY_ORBIT_SPEED: number = _GLOWFLY_ORBIT_SPEED;

/** Discriminated union всех собираемых объектов. */
export type Collectible = Seed | Sand | Flower | Glowfly;

/** Moonleaf-семя — видимо только при glow, собирается в радиусе. */
export interface Seed {
  readonly type: "seed";
  readonly id: EntityId;
  readonly position: ReadonlyVec2;
  collected: boolean;
}

/** Glow-sand — восстанавливает энергию. */
export interface Sand {
  readonly type: "sand";
  readonly id: EntityId;
  readonly position: ReadonlyVec2;
  collected: boolean;
}

/** Glow-flower — восстанавливает бо́льшую порцию энергии. */
export interface Flower {
  readonly type: "flower";
  readonly id: EntityId;
  readonly position: ReadonlyVec2;
  used: boolean;
}

/**
 * Glowfly — дружелюбная летающая искорка, орбитой вокруг basePosition.
 * position мутабельна (обновляется каждый тик), basePosition фиксирован.
 */
export interface Glowfly {
  readonly type: "glowfly";
  readonly id: EntityId;
  readonly basePosition: ReadonlyVec2;
  position: Vec2;
  /** Накопленная фаза для orbit-anim (radians). */
  phase: number;
  collected: boolean;
}

/** Type guard для Seed. */
export const isSeed = (c: Collectible): c is Seed => c.type === "seed";
/** Type guard для Sand. */
export const isSand = (c: Collectible): c is Sand => c.type === "sand";
/** Type guard для Flower. */
export const isFlower = (c: Collectible): c is Flower => c.type === "flower";
/** Type guard для Glowfly. */
export const isGlowfly = (c: Collectible): c is Glowfly => c.type === "glowfly";

/** Создаёт семя по id и позиции. */
export const createSeed = (id: string, position: ReadonlyVec2): Seed => ({
  type: "seed",
  id: asEntityId(id),
  position,
  collected: false,
});

/** Создаёт sand-объект по id и позиции. */
export const createSand = (id: string, position: ReadonlyVec2): Sand => ({
  type: "sand",
  id: asEntityId(id),
  position,
  collected: false,
});

/** Создаёт цветок по id и позиции. */
export const createFlower = (id: string, position: ReadonlyVec2): Flower => ({
  type: "flower",
  id: asEntityId(id),
  position,
  used: false,
});

/** Создаёт glowfly с базовой позицией и случайной начальной фазой. */
export const createGlowfly = (
  id: string,
  basePosition: ReadonlyVec2,
  initialPhase = Math.random() * Math.PI * 2,
): Glowfly => ({
  type: "glowfly",
  id: asEntityId(id),
  basePosition,
  position: { ...basePosition },
  phase: initialPhase,
  collected: false,
});
